import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'


// ─── WebCard extension types (Chrome) ────────────────────────────────────────

interface WebcardReader {
  name: string
  atr?: number[] | string
  connect(shared?: boolean): Promise<number[] | string>
  disconnect(): Promise<void>
  transceive(apduHex: string): Promise<string | number[]>
}

interface WebcardAPI {
  isReady: boolean
  readers(): Promise<WebcardReader[]>
  cardInserted?: (reader: WebcardReader) => void
  cardRemoved?: (event: unknown) => void
  readersConnected?: () => void
  readersDisconnected?: () => void
}

declare global {
  interface Navigator {
    webcard?: WebcardAPI
  }
}

// ─── HTTP Bridge reader (Firefox) ────────────────────────────────────────────

export const BRIDGE_URL = 'http://localhost:7000'

class HttpCardReader implements WebcardReader {
  name: string
  atr: string = ''
  private readerIndex: number

  constructor(index: number, name: string, atr = '') {
    this.readerIndex = index
    this.name = name
    this.atr = atr
  }

  async connect(_shared?: boolean): Promise<string> {
    const res = await fetch(`${BRIDGE_URL}/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reader: this.readerIndex }),
    })
    const data = await res.json()
    if (!res.ok || data.error) throw new Error(data.error ?? 'Connect failed')
    this.atr = data.atr
    return data.atr as string
  }

  async disconnect(): Promise<void> {
    try {
      await fetch(`${BRIDGE_URL}/disconnect`, { method: 'POST' })
    } catch {
      // ignore — might already be disconnected
    }
  }

  async transceive(apduHex: string): Promise<string> {
    const res = await fetch(`${BRIDGE_URL}/transceive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apdu: apduHex }),
    })
    const data = await res.json()
    if (!res.ok || data.error) throw new Error(data.error ?? 'Transceive failed')
    return data.data as string
  }
}

// ─── Browser detection ───────────────────────────────────────────────────────

const isFirefox = () => navigator.userAgent.toLowerCase().includes('firefox')

// ─── Constants ──────────────────────────────────────────────────────────────

const AID_GEN1 = [0xff, 0x54, 0x41, 0x43, 0x48, 0x4f]
const AID_GEN2 = [0xff, 0x53, 0x4d, 0x52, 0x44, 0x54]

interface EfDefinition {
  fid: number
  name: string
  size1: number | null
  size2: number | null
  hash: boolean
}

const EF_LIST: EfDefinition[] = [
  { fid: 0x0002, name: 'EF_ICC', size1: 25, size2: 25, hash: false },
  { fid: 0x0005, name: 'EF_IC', size1: 8, size2: 8, hash: false },
  { fid: 0x0501, name: 'EF_APPLICATION_IDENTIFICATION', size1: 10, size2: 17, hash: true },
  { fid: 0xc100, name: 'EF_CARD_CERTIFICATE', size1: 194, size2: 204, hash: false },
  { fid: 0xc101, name: 'EF_SIGN_CERTIFICATE', size1: 194, size2: 204, hash: false },
  { fid: 0xc108, name: 'EF_CA_CERTIFICATE', size1: 194, size2: 204, hash: false },
  { fid: 0xc109, name: 'EF_LINK_CERTIFICATE', size1: null, size2: 204, hash: false },
  { fid: 0x0520, name: 'EF_IDENTIFICATION', size1: 143, size2: 143, hash: true },
  { fid: 0x050e, name: 'EF_CARD_DOWNLOAD', size1: 4, size2: 4, hash: true },
  { fid: 0x0521, name: 'EF_DRIVING_LICENCE_INFO', size1: 53, size2: 53, hash: true },
  { fid: 0x0502, name: 'EF_EVENTS_DATA', size1: 1728, size2: 3168, hash: true },
  { fid: 0x0503, name: 'EF_FAULTS_DATA', size1: 1152, size2: 1152, hash: true },
  { fid: 0x0504, name: 'EF_DRIVER_ACTIVITY_DATA', size1: 13780, size2: 13780, hash: true },
  { fid: 0x0505, name: 'EF_VEHICLES_USED', size1: 6202, size2: 9602, hash: true },
  { fid: 0x0506, name: 'EF_PLACES', size1: 1121, size2: 2354, hash: true },
  { fid: 0x0507, name: 'EF_CURRENT_USAGE', size1: null, size2: 19, hash: true },
  { fid: 0x0508, name: 'EF_CONTROL_ACTIVITY_DATA', size1: 46, size2: 46, hash: true },
  { fid: 0x0522, name: 'EF_SPECIFIC_CONDITIONS', size1: 280, size2: 562, hash: true },
  { fid: 0x0523, name: 'EF_VEHICLEUNITS_USED', size1: null, size2: 2002, hash: true },
  { fid: 0x0524, name: 'EF_GNSS_PLACES', size1: null, size2: 5042, hash: true },
]

// ─── State types ─────────────────────────────────────────────────────────────

export type WebcardStatus =
  | 'no-extension'    // Chrome: extension not installed
  | 'ws-connecting'   // Firefox: checking if bridge is running
  | 'ws-unavailable'  // Firefox: bridge not reachable
  | 'no-reader'
  | 'idle'
  | 'card-detected'
  | 'reading'
  | 'done'
  | 'error'

export interface EfFileStatus {
  fid: number
  name: string
  status: 'pending' | 'reading' | 'found' | 'missing'
  bytes?: number
}

export interface WebcardLogEntry {
  id: number
  message: string
  type: 'info' | 'ok' | 'warn' | 'err'
}

export interface CardInfo {
  [key: string]: string
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useWebcardReader = () => {
  const { t } = useLocalize()
  const [status, setStatus] = React.useState<WebcardStatus>(
    isFirefox() ? 'ws-connecting' : 'idle'
  )
  const [progress, setProgress] = React.useState(0)
  const [logs, setLogs] = React.useState<WebcardLogEntry[]>([])
  const [efFiles, setEfFiles] = React.useState<EfFileStatus[]>(
    EF_LIST.map((ef) => ({ fid: ef.fid, name: ef.name, status: 'pending' }))
  )
  const [cardInfo, setCardInfo] = React.useState<CardInfo>({})
  const [dddData, setDddData] = React.useState<Uint8Array | null>(null)
  // Incrementing this causes useEffect to re-run — used by retry()
  const [retryKey, setRetryKey] = React.useState(0)
  const readerRef = React.useRef<WebcardReader | null>(null)
  const logIdRef = React.useRef(0)

  const addLog = React.useCallback((message: string, type: WebcardLogEntry['type'] = 'info') => {
    setLogs((prev) => [...prev, { id: logIdRef.current++, message, type }])
    const fn = type === 'err' ? console.error : type === 'warn' ? console.warn : console.log
    fn(`[WebCard] ${message}`)
  }, [])

  const updateEfStatus = React.useCallback(
    (fid: number, efStatus: EfFileStatus['status'], bytes?: number) => {
      setEfFiles((prev) =>
        prev.map((ef) => (ef.fid === fid ? { ...ef, status: efStatus, bytes } : ef))
      )
    },
    []
  )

  // ─── APDU helpers ───────────────────────────────────────────────────────────

  const toBytes = (val: string | number[] | Uint8Array | null | undefined): number[] => {
    if (!val) return []
    if (typeof val === 'string') {
      const clean = val.replace(/\s/g, '')
      const pairs = clean.match(/.{2}/g)
      if (!pairs) return []
      return pairs.map((h) => parseInt(h, 16))
    }
    return Array.from(val)
  }

  const send = React.useCallback(async (apduBytes: number[]): Promise<number[]> => {
    if (!readerRef.current) return [0x6f, 0x00]
    try {
      const hexApdu = apduBytes.map((b) => b.toString(16).padStart(2, '0')).join('')
      const raw = await readerRef.current.transceive(hexApdu)
      return toBytes(raw as string | number[])
    } catch {
      return [0x6f, 0x00]
    }
  }, [])

  const swParse = (resp: number[]) => ({
    sw1: resp[resp.length - 2],
    sw2: resp[resp.length - 1],
    data: resp.slice(0, resp.length - 2),
  })

  const selectApp = React.useCallback(
    async (aid: number[]): Promise<boolean> => {
      for (const p2 of [0x0c, 0x00]) {
        const r = swParse(await send([0x00, 0xa4, 0x04, p2, aid.length, ...aid]))
        if (r.sw1 === 0x90 || r.sw1 === 0x61) return true
      }
      return false
    },
    [send]
  )

  const selectEF = React.useCallback(
    async (fid: number): Promise<boolean> => {
      const hi = (fid >> 8) & 0xff
      const lo = fid & 0xff
      for (const p2 of [0x0c, 0x00]) {
        const r = swParse(await send([0x00, 0xa4, 0x02, p2, 0x02, hi, lo]))
        if (r.sw1 === 0x90 || r.sw1 === 0x61) return true
      }
      return false
    },
    [send]
  )

  const readBinaryExact = React.useCallback(
    async (totalSize: number): Promise<number[]> => {
      const result: number[] = []
      let offset = 0
      const CHUNK = 253
      while (offset < totalSize) {
        const toRead = Math.min(CHUNK, totalSize - offset)
        const p1 = (offset >> 8) & 0x7f
        const p2 = offset & 0xff
        const r = swParse(await send([0x00, 0xb0, p1, p2, toRead]))
        if (r.sw1 === 0x90 && r.data.length > 0) {
          result.push(...r.data)
          offset += r.data.length
        } else break
      }
      return result
    },
    [send]
  )

  const computeHash = React.useCallback(async (): Promise<void> => {
    await send([0x80, 0x2a, 0x90, 0x00])
  }, [send])

  // Write current UTC timestamp to the currently-selected EF_Card_Download file.
  // Must be called immediately after reading EF_Card_Download (while it's still selected).
  // APDU: UPDATE BINARY  00 D6 00 00 04 [4-byte big-endian Unix seconds]
  const writeCardDownload = React.useCallback(async (): Promise<void> => {
    const sec = Math.floor(Date.now() / 1000)
    const b = [(sec >>> 24) & 0xff, (sec >>> 16) & 0xff, (sec >>> 8) & 0xff, sec & 0xff]
    const r = swParse(await send([0x00, 0xd6, 0x00, 0x00, 0x04, ...b]))
    if (r.sw1 === 0x90) {
      addLog(t('importModule.wc-log-date-saved'), 'ok')
    } else {
      addLog(`${t('common.warning')}: ${t('importModule.wc-log-date-save-fail')} (SW ${r.sw1.toString(16).toUpperCase()}${r.sw2.toString(16).toUpperCase()})`, 'warn')
    }
  }, [addLog, send, t])

  const getSignature = React.useCallback(
    async (hashSize: number): Promise<number[] | null> => {
      const r = swParse(await send([0x00, 0x2a, 0x9e, 0x9a, hashSize]))
      return r.sw1 === 0x90 ? r.data : null
    },
    [send]
  )

  // ─── DDD builder ────────────────────────────────────────────────────────────

  const makeDDDRecord = (fid: number, fileType: number, data: number[]): number[] => {
    const hi = (fid >> 8) & 0xff
    const lo = fid & 0xff
    const lenHi = (data.length >> 8) & 0xff
    const lenLo = data.length & 0xff
    return [hi, lo, fileType, lenHi, lenLo, ...data]
  }

  // ─── Card info decoders ─────────────────────────────────────────────────────

  const decodeEfIcc = (data: number[]): CardInfo => {
    if (data.length < 9) return {}
    const types: Record<number, string> = {
      1: t('importModule.wc-card-driver') + ' (Gen1)',
      2: t('importModule.wc-card-workshop') + ' (Gen1)',
      3: t('importModule.wc-card-control') + ' (Gen1)',
      4: t('importModule.wc-card-company') + ' (Gen1)',
      6: t('importModule.wc-card-driver') + ' (Gen2)',
      7: t('importModule.wc-card-workshop') + ' (Gen2)',
      8: t('importModule.wc-card-control') + ' (Gen2)',
      9: t('importModule.wc-card-company') + ' (Gen2)',
    }
    return {
      [t('importModule.wc-card-type')]: types[data[6]] ?? `${t('importModule.unknown')} (0x${data[6].toString(16)})`,
      [t('importModule.wc-generation')]: data[6] >= 6 ? 'Gen2 (Smart Tachograph)' : 'Gen1',
    }
  }

  const decodeEfIdentification = (data: number[]): CardInfo => {
    try {
      const name = String.fromCharCode(...data.slice(0, 36)).trim().replace(/\0/g, '')
      const ts2date = (ts: number) =>
        ts > 0 ? new Date(ts * 1000).toLocaleDateString('pl-PL') : '-'
      const birth = ((data[36] << 24) | (data[37] << 16) | (data[38] << 8) | data[39]) >>> 0
      const expiry = ((data[44] << 24) | (data[45] << 16) | (data[46] << 8) | data[47]) >>> 0
      return {
        [t('importModule.wc-full-name')]: name || '-',
        [t('importModule.wc-birth-date')]: ts2date(birth),
        [t('importModule.wc-valid-until')]: ts2date(expiry),
      }
    } catch {
      return {}
    }
  }

  // ─── Read one generation ─────────────────────────────────────────────────────

  const readGeneration = React.useCallback(
    async (genNum: 1 | 2, dddRecords: number[][], localCardInfo: CardInfo): Promise<number> => {
      const isGen2 = genNum === 2
      const dataType = isGen2 ? 0x02 : 0x00
      const sigType = isGen2 ? 0x03 : 0x01
      const hashSize = isGen2 ? 0x40 : 0x80
      let found = 0

      for (let i = 0; i < EF_LIST.length; i++) {
        const ef = EF_LIST[i]
        const size = isGen2 ? ef.size2 : ef.size1
        if (size === null) continue

        setProgress(5 + Math.round((i / EF_LIST.length) * 85))
        updateEfStatus(ef.fid, 'reading')

        if (!(await selectEF(ef.fid))) {
          updateEfStatus(ef.fid, 'missing')
          continue
        }

        if (ef.hash) await computeHash()

        const data = await readBinaryExact(size)
        if (!data || data.length === 0) {
          updateEfStatus(ef.fid, 'missing')
          continue
        }

        updateEfStatus(ef.fid, 'found', data.length)
        addLog(`[+] ${ef.name} (${data.length} B)`, 'ok')
        dddRecords.push(makeDDDRecord(ef.fid, dataType, data))
        found++

        if (ef.hash) {
          const sig = await getSignature(hashSize)
          if (sig && sig.length > 0) {
            dddRecords.push(makeDDDRecord(ef.fid, sigType, sig))
          }
        }

        // Write read timestamp back to card — must happen right after reading EF_Card_Download
        // while the file is still the currently selected EF (same as C# SC_UpdateCurrentFileEF)
        if (ef.fid === 0x050e) {
          await writeCardDownload()
        }

        if (ef.fid === 0x0002) Object.assign(localCardInfo, decodeEfIcc(data))
        if (ef.fid === 0x0520) Object.assign(localCardInfo, decodeEfIdentification(data))
      }

      return found
    },
    [addLog, computeHash, getSignature, readBinaryExact, selectEF, updateEfStatus, writeCardDownload]
  )

  // ─── Main read function ──────────────────────────────────────────────────────

  const readCard = React.useCallback(async (): Promise<Uint8Array | null> => {
    setStatus('reading')
    setProgress(0)
    setDddData(null)
    setEfFiles(EF_LIST.map((ef) => ({ fid: ef.fid, name: ef.name, status: 'pending' })))
    setLogs([])
    addLog(t('importModule.wc-log-start-reading'), 'info')

    let connected = false
    try {
      const atr = await readerRef.current!.connect(false)
      connected = true
      addLog(
        `ATR: ${toBytes(atr as string)
          .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
          .join(' ')}`,
        'ok'
      )

      const dddRecords: number[][] = []
      const localCardInfo: CardInfo = {}
      let totalFound = 0

      addLog(t('importModule.wc-log-trying-gen1'), 'info')
      if (await selectApp(AID_GEN1)) {
        addLog(t('importModule.wc-log-gen1-selected'), 'ok')
        const n = await readGeneration(1, dddRecords, localCardInfo)
        totalFound += n
        addLog(`Gen1: ${n} ${t('importModule.wc-log-files')}`, n > 0 ? 'ok' : 'warn')
      } else {
        addLog(t('importModule.wc-log-gen1-unavailable'), 'warn')
      }

      addLog(t('importModule.wc-log-trying-gen2'), 'info')
      if (await selectApp(AID_GEN2)) {
        addLog(t('importModule.wc-log-gen2-selected'), 'ok')
        const n = await readGeneration(2, dddRecords, localCardInfo)
        totalFound += n
        addLog(`Gen2: ${n} ${t('importModule.wc-log-files')}`, n > 0 ? 'ok' : 'warn')
      } else {
        addLog(t('importModule.wc-log-gen2-unavailable'), 'warn')
      }

      setProgress(100)

      if (totalFound === 0) {
        setStatus('error')
        addLog(t('importModule.wc-log-no-data'), 'err')
        return null
      }

      setCardInfo(localCardInfo)
      const flat = dddRecords.flat()
      const result = new Uint8Array(flat)
      setDddData(result)
      setStatus('done')
      addLog(`${t('importModule.wc-log-done')} – ${result.length} ${t('importModule.wc-log-bytes')}`, 'ok')
      return result
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setStatus('error')
      addLog(`${t('common.error')}: ` + msg, 'err')
      return null
    } finally {
      if (connected) {
        try {
          await readerRef.current?.disconnect()
        } catch {
          // ignore
        }
      }
    }
  }, [addLog, readGeneration, selectApp, t])

  // ─── Reset / Retry ───────────────────────────────────────────────────────────

  const reset = React.useCallback(() => {
    setProgress(0)
    setLogs([])
    setDddData(null)
    setCardInfo({})
    setEfFiles(EF_LIST.map((ef) => ({ fid: ef.fid, name: ef.name, status: 'pending' })))
    setStatus(isFirefox() ? 'ws-connecting' : 'idle')
  }, [])

  // Re-triggers the init useEffect (for Firefox bridge retry)
  const retry = React.useCallback(() => {
    reset()
    setRetryKey((k) => k + 1)
  }, [reset])

  // ─── Init: Chrome (WebCard extension) ────────────────────────────────────────

  const initChrome = React.useCallback(async (cancelled: { current: boolean }) => {
    const suppressWebcardRejections = (event: PromiseRejectionEvent) => {
      if (event.reason === undefined || event.reason === null) event.preventDefault()
    }

    const loadScript = (): Promise<void> =>
      new Promise((resolve) => {
        if (document.getElementById('webcard-iife-script')) {
          resolve()
          return
        }
        window.addEventListener('unhandledrejection', suppressWebcardRejections)
        const script = document.createElement('script')
        script.id = 'webcard-iife-script'
        script.src = '/assets/webcard.iife.js'
        script.onload = () => {
          setTimeout(
            () => window.removeEventListener('unhandledrejection', suppressWebcardRejections),
            2000
          )
          resolve()
        }
        script.onerror = () => {
          window.removeEventListener('unhandledrejection', suppressWebcardRejections)
          resolve()
        }
        document.head.appendChild(script)
      })

    await loadScript()
    await new Promise((r) => setTimeout(r, 600))
    if (cancelled.current) return

    // Hide the full-page install banner injected by webcard.iife.js
    const banner = document.getElementById('webcard-install-banner')
    if (banner) banner.style.display = 'none'

    if (!navigator.webcard?.isReady) {
      setStatus('no-extension')
      return
    }

    const refreshReaders = async () => {
      if (cancelled.current) return
      try {
        const readers = await navigator.webcard!.readers()
        if (!readers.length) {
          setStatus('no-reader')
          readerRef.current = null
          return
        }
        readerRef.current = readers[0]
        const hasCard = readers[0].atr && toBytes(readers[0].atr as string).length > 0
        setStatus(hasCard ? 'card-detected' : 'idle')
      } catch {
        setStatus('no-reader')
      }
    }

    navigator.webcard.cardInserted = (r: WebcardReader) => {
      if (cancelled.current) return
      readerRef.current = r
      setStatus('card-detected')
    }
    navigator.webcard.cardRemoved = () => {
      if (cancelled.current) return
      setStatus((prev) => (prev === 'reading' ? prev : 'idle'))
    }
    navigator.webcard.readersConnected = refreshReaders
    navigator.webcard.readersDisconnected = refreshReaders

    await refreshReaders()
  }, [])

  // ─── Init: Firefox (HTTP bridge) ─────────────────────────────────────────────

  const initFirefox = React.useCallback(async (cancelled: { current: boolean }) => {
    // AbortController + setTimeout instead of AbortSignal.timeout() for wider compatibility
    const pingController = new AbortController()
    const pingTimeout = setTimeout(() => pingController.abort(), 2000)

    try {
      const res = await fetch(`${BRIDGE_URL}/ping`, { signal: pingController.signal })
      clearTimeout(pingTimeout)
      if (!res.ok) throw new Error('Bridge returned non-OK')
    } catch {
      clearTimeout(pingTimeout)
      if (!cancelled.current) setStatus('ws-unavailable')
      return
    }

    if (cancelled.current) return

    try {
      const res = await fetch(`${BRIDGE_URL}/readers`)
      const data = await res.json()
      const readers: Array<{ index: number; name: string; atr: string }> = data.readers ?? []

      if (!readers.length) {
        setStatus('no-reader')
        return
      }

      const first = readers[0]
      readerRef.current = new HttpCardReader(first.index, first.name, first.atr)
      setStatus(first.atr ? 'card-detected' : 'idle')
    } catch {
      if (!cancelled.current) setStatus('no-reader')
    }
  }, [])

  // ─── Effect: initialise (re-runs on retry) ───────────────────────────────────

  React.useEffect(() => {
    const cancelled = { current: false }

    if (isFirefox()) {
      initFirefox(cancelled)
    } else {
      initChrome(cancelled)
    }

    return () => {
      cancelled.current = true
    }
  // retryKey causes this effect to re-run when the user clicks retry
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey])

  return {
    status,
    progress,
    logs,
    efFiles,
    cardInfo,
    dddData,
    readCard,
    reset,
    retry,
    isFirefox: isFirefox(),
  }
}
